import {
    type AgentConversationRead,
    type ApiResponseType,
} from '@libraries/typescript/api_client';

export type OrganizationAgentMessageCreateResponse =
    | ApiResponseType<
          '/organizations/{organization_id}/runs/{run_id}/messages',
          'post'
      >
    | ApiResponseType<
          '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/messages',
          'post'
      >;

export type OrganizationAgentMessageCreateResponseAction =
    | {
          readonly kind: 'event-layer';
          readonly phoebeRunId: string;
      }
    | {
          readonly kind: 'legacy';
          readonly turnId: string;
      }
    | {
          readonly kind: 'invalid';
      };

export type AgentConversationTurnRead = AgentConversationRead['turns'][number];

export type AgentTranscriptSource = {
    readonly turns: AgentConversationRead['turns'];
};

export type BuildOptimisticAgentConversationTurnParams = {
    readonly conversation: AgentTranscriptSource | null | undefined;
    readonly turnId: string;
    readonly text: string;
};

export function getOrganizationAgentMessageCreateResponseAction(
    response: OrganizationAgentMessageCreateResponse | null | undefined,
): OrganizationAgentMessageCreateResponseAction {
    if (
        response &&
        'run_id' in response &&
        response.run_id !== null &&
        response.run_id !== undefined
    ) {
        return {
            kind: 'event-layer',
            phoebeRunId: response.run_id,
        };
    }

    if (
        response &&
        'phoebe_run_id' in response &&
        response.phoebe_run_id !== null &&
        response.phoebe_run_id !== undefined
    ) {
        return {
            kind: 'event-layer',
            phoebeRunId: response.phoebe_run_id,
        };
    }

    if (response?.turn_id !== null && response?.turn_id !== undefined) {
        return {
            kind: 'legacy',
            turnId: response.turn_id,
        };
    }

    return { kind: 'invalid' };
}

export function buildOptimisticAgentConversationTurn({
    conversation,
    turnId,
    text,
}: BuildOptimisticAgentConversationTurnParams): AgentConversationTurnRead {
    const createdAt = new Date().toISOString();
    const maxSequence = Math.max(
        0,
        ...(conversation?.turns ?? []).map((turn) => turn.sequence),
    );

    return {
        id: turnId,
        sequence: maxSequence + 1,
        created_at: createdAt,
        updated_at: createdAt,
        provider_response_id: null,
        items: [
            {
                id: `optimistic-${turnId}-user-message`,
                created_at: createdAt,
                direction: 'input',
                role: 'user',
                type: 'text',
                index_in_turn: 0,
                text_content: text,
                call_id: null,
                tool_name: null,
                tool_status: null,
                arguments_json: null,
                output_json: null,
                raw_json: null,
            },
        ],
    };
}
